import type { NamespaceSelectionCapability, ScopeResponse } from "@k8s-dashboard/contracts";

export const NAMESPACE_SELECTION_QUERY = "namespaceSelection";
export const NAMESPACE_SELECTION_VERSION_HEADER = "Observer-Namespace-Selection-Version";
export const NAMESPACE_SELECTION_BINDING_HEADER = "Observer-Namespace-Selection-Binding";
export const NAMESPACE_SELECTION_VERSION = "1";
export const NAMESPACE_SELECTION_MAX_MEMBERS = 10;
export const NAMESPACE_SELECTION_MAX_NAME_BYTES = 63;
export const NAMESPACE_SELECTION_MAX_NAMES_BYTES = 630;
export const NAMESPACE_SELECTION_MAX_TOKENS_BYTES = 660;
export const NAMESPACE_SELECTION_MAX_BINDING_BYTES = 669;
export const NAMESPACE_SELECTION_MAX_HEADERS_BYTES = 750;

export type NamespaceSelection =
  | Readonly<{ mode: "all" }>
  | Readonly<{ mode: "selected"; names: readonly string[] }>;

export type NamespaceSelectionTransport =
  | Readonly<{ mode: "new"; selection: NamespaceSelection; binding: string; params: readonly (readonly [string, string])[] }>
  | Readonly<{ mode: "legacy-all"; selection: NamespaceSelection; binding: string; params: readonly [] }>
  | Readonly<{ mode: "legacy-single"; selection: NamespaceSelection; binding: string; params: readonly (readonly [string, string])[] }>
  | Readonly<{ mode: "blocked"; selection: NamespaceSelection; binding: string; params: readonly [] }>;

export function isNamespaceName(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= NAMESPACE_SELECTION_MAX_NAME_BYTES &&
    /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(value);
}

export function canonicalNamespaceSelection(value: unknown): NamespaceSelection | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const candidate = value as { mode?: unknown; names?: unknown };
  const keys = Object.keys(value);
  if (candidate.mode === "all") return keys.length === 1 && keys[0] === "mode" ? { mode: "all" } : undefined;
  if (candidate.mode !== "selected" || !Array.isArray(candidate.names) ||
      keys.length !== 2 || !keys.includes("mode") || !keys.includes("names") ||
      candidate.names.length === 0 || candidate.names.length > NAMESPACE_SELECTION_MAX_MEMBERS) return undefined;
  let namesBytes = 0;
  const names = [...candidate.names];
  for (const name of names) {
    if (!isNamespaceName(name)) return undefined;
    namesBytes += name.length;
  }
  if (namesBytes > NAMESPACE_SELECTION_MAX_NAMES_BYTES || new Set(names).size !== names.length) return undefined;
  names.sort();
  const selection = { mode: "selected", names: Object.freeze(names) } as const;
  const tokensBytes = names.reduce((total, name) => total + 3 + name.length, 0);
  const bindingBytes = namespaceSelectionBinding(selection).length;
  return tokensBytes <= NAMESPACE_SELECTION_MAX_TOKENS_BYTES && bindingBytes <= NAMESPACE_SELECTION_MAX_BINDING_BYTES
    ? selection
    : undefined;
}

export function namespaceSelectionBinding(selection: NamespaceSelection): string {
  return selection.mode === "all" ? "all" : selection.names.map((name) => `ns:${name}`).join(",");
}

export function namespaceSelectionLabel(selection: NamespaceSelection): string {
  if (selection.mode === "all") return "all";
  return selection.names.length === 1 ? selection.names[0] : selection.names.join(", ");
}

export function isNamespaceSelectionCapability(value: unknown): value is NamespaceSelectionCapability {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const capability = value as Partial<NamespaceSelectionCapability>;
  return capability.version === 1 && capability.maxMembers === NAMESPACE_SELECTION_MAX_MEMBERS &&
    capability.maxNameBytes === NAMESPACE_SELECTION_MAX_NAME_BYTES && capability.maxNamesBytes === NAMESPACE_SELECTION_MAX_NAMES_BYTES &&
    capability.maxTokensBytes === NAMESPACE_SELECTION_MAX_TOKENS_BYTES && capability.maxBindingBytes === NAMESPACE_SELECTION_MAX_BINDING_BYTES &&
    capability.maxHeadersBytes === NAMESPACE_SELECTION_MAX_HEADERS_BYTES;
}

export function namespaceSelectionTransport(
  selection: NamespaceSelection,
  capability: unknown,
  legacyKey: "ns" | "namespace" = "ns",
): NamespaceSelectionTransport {
  const canonical = canonicalNamespaceSelection(selection);
  if (!canonical) return { mode: "blocked", selection, binding: "invalid", params: [] };
  const binding = namespaceSelectionBinding(canonical);
  if (isNamespaceSelectionCapability(capability)) {
    const values = canonical.mode === "all" ? ["all"] : canonical.names.map((name) => `ns:${name}`);
    return { mode: "new", selection: canonical, binding, params: values.map((value) => [NAMESPACE_SELECTION_QUERY, value] as const) };
  }
  if (canonical.mode === "all") {
    return { mode: "legacy-all", selection: canonical, binding, params: [] };
  }
  if (canonical.mode === "selected" && canonical.names.length === 1 && canonical.names[0] !== "all") {
    return { mode: "legacy-single", selection: canonical, binding, params: [[legacyKey, canonical.names[0]]] };
  }
  return { mode: "blocked", selection: canonical, binding, params: [] };
}

export function selectionAuthorized(scope: ScopeResponse | undefined, clusterId: string, selection: NamespaceSelection): boolean {
  const canonical = canonicalNamespaceSelection(selection);
  if (!canonical) return false;
  const cluster = scope?.clusters.find((candidate) => candidate.id === clusterId);
  if (!cluster?.accessible) return false;
  if (canonical.mode === "all") return true;
  return canonical.names.every((name) => cluster.namespaces === "all" || cluster.namespaces.includes(name));
}

export function scopeFingerprint(scope: ScopeResponse | undefined, clusterId: string): string {
  const cluster = scope?.clusters.find((item) => item.id === clusterId);
  if (!cluster) return "missing";
  const namespaces = cluster.namespaces === "all" ? "all" : [...cluster.namespaces].sort().join("\u001f");
  return `${cluster.id.length}:${cluster.id}|${cluster.accessible ? 1 : 0}|${namespaces.length}:${namespaces}`;
}

export function acknowledgementMatches(headers: Headers, binding: string): boolean {
  return headers.get(NAMESPACE_SELECTION_VERSION_HEADER) === NAMESPACE_SELECTION_VERSION &&
    headers.get(NAMESPACE_SELECTION_BINDING_HEADER) === binding;
}
